// pages/MomentDetailPage.jsx — Anamoria SPA
// v0.13 — NEW-105 (2026-09-26): cap the edit-day input at DAY_MAX = 40 chars.
// v0.12 — W2 polish (Sept 10, 2026): removed the moment-level "add to every piece" box from
//   the detail view (premature on an existing moment; deferred to the save/create flow). The
//   AddTagToAll component stays in MemoryTags, dormant, for that follow-on. Per-piece tagging unchanged.
// v0.11 — W2 member tags UI refinement (Sept 10, 2026): per-piece MemoryTags moved ABOVE the
//   favorite/share row so tags sit directly under the subject (Gestalt proximity — a tag is a
//   property of the piece; favorite/share are actions on it). Order-only change, no logic touched.
// v0.10 — W2 member tags (Sept 8, 2026): per-piece MemoryTags (add/remove chips) beside the
//   favorite/share controls on text/spotlight/audio pieces, + optional moment-level AddTagToAll
//   shortcut; gated on member_tags_enabled. Optimistic remove / server-reconciled add; PATCH
//   /memories/:id { addTag | removeTagId }. Quiet failure line (incl. the 5-tag limit). Rail-thumb
//   pieces are a follow-on. Flag off => byte-identical (nothing rendered).
// v0.9 — DF-7 feed chrome: per-piece favorite heart glyph 16 -> 20px.
// v0.8 — per-piece sharing in the Memory View reading view (feed_redesign_v2).
//   Each piece (text / spotlight / audio) gets a Private/Shared status that is
//   also the control: tapping it flips memory.isPrivate via PATCH /memories/:id
//   (owner-scoped server-side), optimistic + revert-on-failure like favorites,
//   with a quiet failure line (sharing must not fail silently). Feed cache
//   invalidated so the Private/Shared tabs reflect the change. Labels
//   "Private"/"Shared" (Sophie 2026-07-31). Per momentdetail-share-status-plan
//   v0.2. Rail thumbnails + lightbox share, and a confirm-on-expose guardrail,
//   are deferred follow-ups (see plan). Flag-off path unchanged.
// v0.7 — per-piece favorites in the Memory View reading view (feed_redesign_v2).
//   Each piece (text / spotlight / audio) gets an always-visible favorite heart;
//   rail thumbnails favorite in the lightbox. Optimistic toggle mirrors
//   MemoryFeed.handleFavorite → POST /memories/:id/favorite (owner-scoped
//   server-side); silent revert on failure. Placement per
//   momentdetail-favorites-plan v0.1 + mockup v0.3. Flag-off path unchanged.
// v0.6 — feed_redesign_v2: Memory View by type (spotlight + Text/Video/Audio/
//   Photo), per the reviewed mockup (momentdetail-mockup v0.9). View state only;
//   edit state, top bar, and date byte-identical. Gated on feed_redesign_v2 —
//   flag-off renders the v0.4 stacked piece_order map unchanged. Spotlight =
//   first video (plays inline via VideoCard) else first photo (opens lightbox);
//   audio uses embedded VoiceCards; extra videos/photos are thumbnail rails →
//   MediaLightbox. Partition in utils/momentPieces.js.
// v0.5 — feed_redesign_v2: hero-by-precedence reading view (superseded by v0.6).
// v0.4 — 4.3: whole-memory Remove + view top action bar (July 24, 2026)
//   Executes plan v0.12 (D-4.16 top bar, D-4.17 undo) + copy v0.12 (DS-1..5,
//   DS-10, DS-13). Changes from v0.3:
//   - View actions lift out of the reading body into a .topBar (D-4.16):
//     "Back to memories" left, Edit + Remove grouped right; Remove in the
//     quiet-red register, after Edit. Resolves the odd in-body Edit placement.
//   - Remove opens the reused DeleteConfirmModal (Remove/Keep, moment body),
//     POST /moments/{id}/delete (author-only, enforced server-side; the page
//     only renders when moments_enabled, so no extra UI flag guard) → leave
//     the feed. DS-10 line on failure.
//   - After-remove confirmation (DS-13) is passed to the feed via nav state
//     { removedToast: true } for SpacePage to surface (one-tap undo = D-4.17
//     fast-follow). No behaviour change to Edit/Save.
// v0.3 — 4.1 C2 walkthrough revision: capture-format edit state (July 22, 2026)
//
// Executes plan v0.8 step 4.1 per momentedit-4-1-spa-DRAFT-for-review-v0.5.md
// (W-1/W-2 closed on Sophie's annotated walkthrough; B-3 layout judgment
// revised by its own stated test). Changes from v0.2:
//   - Edit state is now the CAPTURE LAYOUT, in place (W-1): title input →
//     day input (capture field order) → WriteRegion seeded with the existing
//     text → PhotoRegion (existing photos as chips via playbackUrl + adds) →
//     Voice|Video region (existing recordings as chips + adds; existing
//     voice chips play via playbackUrl) → cap line → Save changes/Cancel.
//     The v0.2 hybrid (view-stacked pieces with per-card Remove) is deleted.
//     View state remains byte-identical v0.1 (gate).
//   - Hearth tokens reach the edit state via styles/hearthTokens.module.css
//     on the column while editing only (W-2 — tokens redefine --font-serif
//     et al., so always-on would alter the view).
//   - Unchanged from v0.2: token model (R-rules; unknown categories carried),
//     dirty baseline (§2a.7), A-6 hideRemove, N1 zero-piece pre-empt, S-10
//     guard with Cancel→view / Back→feed destinations, save = uploads →
//     assemblePatchPieces → PATCH; response IS the fresh view (F-B4(a)).
//     Piece order not visible while editing (capture parity) — untouched
//     pieces round-trip in exact server order via tokens; recorded trade.
//   - local formatSec/normalizeMime copies carry the momentPatch v1.1
//     unification cleanup note.
// v0.2 — 4.1: in-place edit, hybrid layout (July 22, 2026; superseded by W-1)
// v0.1 — 3.2 read-only moment detail (July 21, 2026)
//   (view mechanics unchanged: route /spaces/:spaceId/moments/:momentId,
//   GET /moments/{id}, uniform-404 states, quiet loading, F-D2(a)
//   element-anchored back, A-2(a) voice theme, server-order pieces,
//   deriveMomentTitle, dayText-only date.)

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppContext } from '../App';
import { createApiClient } from '../api/client';
import { invalidateMemoriesCache } from '../components/MemoryFeed';
import MemoryTags from '../components/MemoryTags';
import VoiceCard from '../components/VoiceCard';
import VideoCard from '../components/VideoCard';
import { MomentPhoto } from '../components/MomentCard';
import MediaLightbox from '../components/MediaLightbox';
import LeaveConfirmModal from '../components/LeaveConfirmModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import WriteRegion from '../components/capture/WriteRegion';
import PhotoRegion from '../components/capture/PhotoRegion';
import RecordingRegion from '../components/capture/RecordingRegion';
import { assemblePatchPieces, countEditPieces, MAX_PIECES } from '../utils/momentPatch';
import { deriveMomentTitle } from '../utils/momentTitle';
import { splitParagraphs } from '../utils/momentText';
import { partitionMomentPieces } from '../utils/momentPieces';
import { getCached, setCache, SPACE_DETAIL_CACHE_TTL } from '../utils/apiCache';
import styles from './MomentDetailPage.module.css';
import amStyles from './AddMemoryPage.module.css';
import tokenStyles from '../styles/hearthTokens.module.css';

const cat = (p) => (p?.category || '').toLowerCase();

// D5: client-side title cap (capture parity).
const TITLE_MAX = 120;
const DAY_MAX = 40;

// Local copies pending the shared-helper cleanup (momentPatch v1.1 note):
// 2.1b parity (D-2.1b-4) MIME normalization + chip duration formatting.
function normalizeMime(type, fallback) {
  const base = (type || fallback || '').split(';')[0];
  if (base === 'audio/x-m4a') return 'audio/mp4';
  if (base === 'audio/x-wav') return 'audio/wav';
  return base;
}
function formatSec(sec) {
  const m = Math.floor(sec / 60);
  const ss = Math.round(sec % 60);
  return `${m}:${String(ss).padStart(2, '0')}`;
}

// Strings — copy v0.5 (edit-set) + ratified reuse; zero invention.
const EDIT_LABEL = 'Editing this memory';                       // S-2
const SAVE_CHANGES_FAILED_LINE =                                 // S-5
  'We weren’t able to save your changes. They’re still here. When you’re ready, please try again.';
const LEAVE_MESSAGE = "Your changes won't be saved if you leave now."; // S-10
const EMPTY_SAVE_LINE =                                          // N1 (reuse)
  'There’s nothing in this memory yet. When you’re ready, you can add some words, a photo, or a recording.';
const CAP_NOTICE_LINE =                                          // ratified cap line (full-sentence register)
  'A memory can hold up to 10 things. You can save this one and add more in a new memory.';
const REMOVE_FAILED_LINE =                                       // DS-10 (copy v0.12, owner-reviewed)
  'We weren’t able to remove this memory. When you’re ready, please try again.';
const SHARE_FAILED_LINE =                                        // v0.8 — brand-voice register (draft for review)
  'We weren’t able to change who can see this. When you’re ready, please try again.';

let nextLocalId = 1;

// ─── feed_redesign_v2 reading view — spotlight + typed sections ───
//     Text / (spotlight) / Video / Audio / Photo, per momentdetail-mockup v0.9.
//     Spotlight video plays inline (embedded VideoCard); spotlight photo opens
//     the lightbox. Extra videos/photos are thumbnails → lightbox; audio uses
//     the real VoiceCard (embedded) inline.
function VideoThumb({ piece, onOpen }) {
  const poster = piece.videoNote?.posterUrl || null;
  const dur = piece.videoNote?.durationFormatted || piece.durationFormatted || null;
  return (
    <button type="button" className={styles.thumb} onClick={onOpen} aria-label="Play video">
      {poster
        ? <img className={styles.thumbImg} src={poster} alt="" loading="lazy" />
        : <span className={styles.thumbFallback} aria-hidden="true" />}
      <span className={styles.thumbPlay} aria-hidden="true" />
      {dur && <span className={styles.thumbDur}>{dur}</span>}
    </button>
  );
}

function PhotoThumb({ piece, onOpen }) {
  const src = piece.playbackUrl;
  const fallback = piece.originalPlaybackUrl;
  return (
    <button type="button" className={styles.thumb} onClick={onOpen} aria-label="Open photo">
      {src
        ? <img
            className={styles.thumbImg}
            src={src}
            alt=""
            loading="lazy"
            onError={(e) => { if (fallback && e.target.src !== fallback) e.target.src = fallback; }}
          />
        : <span className={styles.thumbFallback} aria-hidden="true" />}
    </button>
  );
}

// ─── Per-piece favorite control (v0.7) ───
//   Legacy HeartIcon path + effects (VoiceCard.jsx / .module.css), but the
//   heart is ALWAYS visible on the detail reading view (no hover-reveal —
//   touch-first PWA; NN/g) with a 44px hit area (WCAG 2.5.8). Overlay variant
//   for media pieces; inline labeled variant for the text piece (no occlusion).
function HeartIcon({ filled }) {
  if (filled) {
    return (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}

// inline: labeled heart under the text piece. otherwise: corner overlay.
// NOTE: inline word copy ("Favorite"/"Favorited") pending brand-voice confirm.
function PieceFavorite({ favorited, onToggle, inline = false }) {
  const label = favorited ? 'Remove from favorites' : 'Add to favorites';
  const handle = (e) => { e.stopPropagation(); onToggle(); };
  if (inline) {
    return (
      <button
        type="button"
        className={`${styles.favInline} ${favorited ? styles.favLiked : ''}`}
        onClick={handle}
        aria-label={label}
        aria-pressed={favorited}
      >
        <span className={styles.favDot}><HeartIcon filled={favorited} /></span>
        <span className={styles.favWord}>{favorited ? 'Favorited' : 'Favorite'}</span>
      </button>
    );
  }
  return (
    <button
      type="button"
      className={`${styles.favOverlay} ${favorited ? styles.favLiked : ''}`}
      onClick={handle}
      aria-label={label}
      aria-pressed={favorited}
    >
      <span className={styles.favDot}><HeartIcon filled={favorited} /></span>
    </button>
  );
}

// ─── Per-piece sharing control (v0.8) ───
//   Reuses the shared privacy glyph (MomentCard PrivacyIcon: lock = private,
//   globe = shared). The status IS the toggle: labels "Private"/"Shared"
//   (Sophie 2026-07-31). Inline (border-less) under the text piece; pill chip
//   beneath media/audio pieces. 44px-tall hit area; aria states current +
//   action (no aria-pressed — it's a two-label control, not on/off).
function PrivacyGlyph({ isPrivate }) {
  if (isPrivate) {
    return (
      <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
        <rect x="5" y="11" width="14" height="10" rx="2" />
        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" />
    </svg>
  );
}

function PieceShare({ isPrivate, onToggle, inline = false }) {
  const label = isPrivate ? 'Only you can see this. Share it.' : 'Shared. Make it private again.';
  const handle = (e) => { e.stopPropagation(); onToggle(); };
  return (
    <button
      type="button"
      className={`${styles.shareChip} ${isPrivate ? '' : styles.shareChipOn} ${inline ? styles.shareChipInline : ''}`}
      onClick={handle}
      aria-label={label}
    >
      <span className={styles.shareGlyph}><PrivacyGlyph isPrivate={isPrivate} /></span>
      <span className={styles.shareWord}>{isPrivate ? 'Private' : 'Shared'}</span>
    </button>
  );
}

function MomentReadingView({ moment, title, voiceTheme, getApi, redesign, onToggleFavorite, onToggleShare,
  memberTagsEnabled = false, onAddTag, onRemoveTag, tagSuggestions = [] }) {
  const { text, spotlight, videos, photos, audios, videoThumbs, photoThumbs } =
    partitionMomentPieces(moment.pieces || []);
  const [photoLb, setPhotoLb] = useState(null); // index into `photos` | null
  const [videoLbId, setVideoLbId] = useState(null); // a video piece id | null
  const photoStart = spotlight && spotlight.kind === 'photo' ? 1 : 0;
  // Resolve the lightbox video from the live partition each render so its
  // favorite state stays current after an optimistic toggle.
  const videoLbItem = videoLbId != null ? videos.find((v) => v.id === videoLbId) : null;
  return (
    <>
      {text?.note && (
        <section className={styles.group}>
          <p className={styles.groupLabel}>Text</p>
          <div className={styles.textPiece}>
            {splitParagraphs(text.note).map((para, i) => (
              <p key={i} className={styles.paragraph}>{para}</p>
            ))}
          </div>
          {memberTagsEnabled && (
            <MemoryTags
              tags={text.tags || []}
              onAdd={(l) => onAddTag(text.id, l)}
              onRemove={(id) => onRemoveTag(text.id, id)}
              suggestions={tagSuggestions}
            />
          )}
          <div className={styles.metaRow}>
            <PieceFavorite
              inline
              favorited={!!text.isFavorite}
              onToggle={() => onToggleFavorite(text.id, text.isFavorite)}
            />
            <PieceShare
              inline
              isPrivate={!!text.isPrivate}
              onToggle={() => onToggleShare(text.id, text.isPrivate)}
            />
          </div>
        </section>
      )}

      {spotlight?.kind === 'video' && (
        <div className={styles.pieceBlock}>
          <div className={`${styles.spotlight} ${styles.favHost}`}>
            <VideoCard memory={spotlight.piece} embedded redesign={redesign} />
            <PieceFavorite
              favorited={!!spotlight.piece.isFavorite}
              onToggle={() => onToggleFavorite(spotlight.piece.id, spotlight.piece.isFavorite)}
            />
          </div>
          {memberTagsEnabled && (
            <MemoryTags
              tags={spotlight.piece.tags || []}
              onAdd={(l) => onAddTag(spotlight.piece.id, l)}
              onRemove={(id) => onRemoveTag(spotlight.piece.id, id)}
              suggestions={tagSuggestions}
            />
          )}
          <div className={styles.pieceMeta}>
            <PieceShare
              isPrivate={!!spotlight.piece.isPrivate}
              onToggle={() => onToggleShare(spotlight.piece.id, spotlight.piece.isPrivate)}
            />
          </div>
        </div>
      )}
      {spotlight?.kind === 'photo' && (
        <div className={styles.pieceBlock}>
          <div className={styles.favHost}>
            <button
              type="button"
              className={styles.spotlightPhoto}
              onClick={() => setPhotoLb(0)}
              aria-label={`${title}, open`}
            >
              <MomentPhoto piece={spotlight.piece} alt={title} />
            </button>
            <PieceFavorite
              favorited={!!spotlight.piece.isFavorite}
              onToggle={() => onToggleFavorite(spotlight.piece.id, spotlight.piece.isFavorite)}
            />
          </div>
          {memberTagsEnabled && (
            <MemoryTags
              tags={spotlight.piece.tags || []}
              onAdd={(l) => onAddTag(spotlight.piece.id, l)}
              onRemove={(id) => onRemoveTag(spotlight.piece.id, id)}
              suggestions={tagSuggestions}
            />
          )}
          <div className={styles.pieceMeta}>
            <PieceShare
              isPrivate={!!spotlight.piece.isPrivate}
              onToggle={() => onToggleShare(spotlight.piece.id, spotlight.piece.isPrivate)}
            />
          </div>
        </div>
      )}

      {videoThumbs.length > 0 && (
        <section className={styles.group}>
          <p className={styles.groupLabel}>Video</p>
          <div className={styles.thumbRail} role="group" aria-label="Video">
            {videoThumbs.map((p) => (
              <VideoThumb key={p.id} piece={p} onOpen={() => setVideoLbId(p.id)} />
            ))}
          </div>
        </section>
      )}

      {audios.length > 0 && (
        <section className={styles.group}>
          <p className={styles.groupLabel}>Audio</p>
          <div className={styles.audioStack}>
            {audios.map((p) => (
              <div key={p.id} className={styles.pieceBlock}>
                <div className={styles.favHost}>
                  <VoiceCard memory={p} getApi={getApi} theme={voiceTheme} embedded redesign={redesign} />
                  <PieceFavorite
                    favorited={!!p.isFavorite}
                    onToggle={() => onToggleFavorite(p.id, p.isFavorite)}
                  />
                </div>
                {memberTagsEnabled && (
                  <MemoryTags
                    tags={p.tags || []}
                    onAdd={(l) => onAddTag(p.id, l)}
                    onRemove={(id) => onRemoveTag(p.id, id)}
                    suggestions={tagSuggestions}
                  />
                )}
                <div className={styles.pieceMeta}>
                  <PieceShare
                    isPrivate={!!p.isPrivate}
                    onToggle={() => onToggleShare(p.id, p.isPrivate)}
                  />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {photoThumbs.length > 0 && (
        <section className={styles.group}>
          <p className={styles.groupLabel}>Photo</p>
          <div className={styles.thumbRail} role="group" aria-label="Photo">
            {photoThumbs.map((p, k) => (
              <PhotoThumb key={p.id} piece={p} onOpen={() => setPhotoLb(photoStart + k)} />
            ))}
          </div>
        </section>
      )}

      {photoLb !== null && (
        <MediaLightbox
          items={photos}
          index={photoLb}
          title={title}
          onIndex={setPhotoLb}
          onClose={() => setPhotoLb(null)}
          onToggleFavorite={onToggleFavorite}
        />
      )}
      {videoLbItem && (
        <MediaLightbox
          items={[videoLbItem]}
          index={0}
          title={title}
          onIndex={() => {}}
          onClose={() => setVideoLbId(null)}
          onToggleFavorite={onToggleFavorite}
        />
      )}
    </>
  );
}

export default function MomentDetailPage() {
  const { spaceId, momentId } = useParams();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const appState = useAppContext();
  const feedRedesignEnabled = appState.flags?.feed_redesign_v2 === true;
  const memberTagsEnabled = appState.flags?.member_tags_enabled === true;

  const [moment, setMoment] = useState(null);
  const [status, setStatus] = useState('loading'); // 'loading'|'ready'|'notfound'|'error'
  const [voiceTheme, setVoiceTheme] = useState('warm'); // A-2(a) fallback

  // ─── v0.3 edit state (D-4.11; W-1 capture-format) ───
  const [editing, setEditing] = useState(false);
  const [eTitle, setETitle] = useState('');
  const [eDay, setEDay] = useState('');
  const [eText, setEText] = useState('');
  const [textPieceId, setTextPieceId] = useState(null);
  const [orderList, setOrderList] = useState([]); // [{kind:'server',id,category}|{kind:'local',localId}]
  const [editPhotos, setEditPhotos] = useState([]);     // [{ id, serverId?, file?, url }]
  const [editRecordings, setEditRecordings] = useState([]); // [{ id, serverId?, kind, blob?, mimeType?, duration, durationFormatted, url }]
  const [editBaseline, setEditBaseline] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [saveNotice, setSaveNotice] = useState(null);   // null|'empty'|'failed'|'cap'
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [pendingExit, setPendingExit] = useState('view'); // 'view' (Cancel) | 'feed' (Back)

  // ─── v0.4 remove state (D-4.16/D-4.17) ───
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState(false);

  // ─── v0.8 per-piece sharing: quiet failure line (sharing must not fail
  //     silently — the owner needs to know who can actually see a piece). ───
  const [shareNotice, setShareNotice] = useState(false);

  // ─── v1.0 (W2) member tags: quiet failure line; 'limit' when the memory is at 5. ───
  const [tagNotice, setTagNotice] = useState(null); // null | true | 'limit'

  const getApi = useCallback(
    () => createApiClient(getAccessTokenSilently),
    [getAccessTokenSilently]
  );

  useEffect(() => {
    let cancelled = false;
    const api = createApiClient(getAccessTokenSilently);

    api.get(`/moments/${momentId}`)
      .then((data) => {
        if (cancelled) return;
        setMoment(data);
        setStatus('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus(err && err.status === 404 ? 'notfound' : 'error');
      });

    // A-2(a): space theme — cache-first via the SpacePage pattern (cached
    // under `space:${spaceId}` seconds earlier on the feed→detail path);
    // direct-URL entry fetches once and warms the same key. Cosmetic only —
    // 'warm' stands until resolved.
    const cachedSpace = getCached(`space:${spaceId}`);
    if (cachedSpace?.voiceCardTheme) {
      setVoiceTheme(cachedSpace.voiceCardTheme);
    } else {
      api.get(`/spaces/${spaceId}`)
        .then((s) => {
          if (cancelled || !s) return;
          setCache(`space:${spaceId}`, s, SPACE_DETAIL_CACHE_TTL);
          if (s.voiceCardTheme) setVoiceTheme(s.voiceCardTheme);
        })
        .catch(() => { /* cosmetic only — warm fallback stands */ });
    }

    return () => { cancelled = true; };
  }, [momentId, spaceId, getAccessTokenSilently]);

  // ─── Edit-state derived values ───
  const pieceCount = countEditPieces(orderList, { text: eText, textPieceId });
  const atCap = pieceCount >= MAX_PIECES;
  const soleRemaining = editing && pieceCount === 1; // A-6

  // NEW-130: split regions each need their own filtered slice; editRecordings
  // itself stays one flat array (unchanged) so handleSaveEdit/orderList logic
  // doesn't need to know about the split at all.
  const voiceEditRecordings = editRecordings.filter((r) => r.kind === 'voice');
  const videoEditRecordings = editRecordings.filter((r) => r.kind === 'video');
  const tokenSig = orderList
    .map((t) => (t.kind === 'server' ? `s${t.id}` : `l${t.localId}`))
    .join('|');
  const dirty = editing && Boolean(editBaseline) && (
    eTitle !== editBaseline.title ||
    eDay !== editBaseline.day ||
    eText !== editBaseline.text ||
    tokenSig !== editBaseline.sig);

  // ─── Enter edit: build the token model + chip entries from the loaded
  //     moment (no refetch). W-1: existing pieces become capture chips. ───
  const handleEnterEdit = () => {
    const tokens = [];
    const photoEntries = [];
    const recEntries = [];
    let txtId = null;
    let txt = '';
    for (const piece of (moment.pieces || [])) {
      const kind = cat(piece);
      if (kind === 'text' && txtId === null) {
        txtId = piece.id;
        txt = piece.note || '';
      } else if (kind === 'photo') {
        photoEntries.push({ id: nextLocalId++, serverId: piece.id, url: piece.playbackUrl });
      } else if (kind === 'voice' || kind === 'video') {
        // NEW-130 fix: `piece.duration`/`piece.playbackUrl` don't exist on a
        // real piece — mapMemoryRow (anamoria-memories) only nests these
        // under `piece.voiceNote`/`piece.videoNote` (confirmed against the
        // live backend; the view screen already reads it this way at line
        // ~389 below, which is exactly why view mode has always shown a
        // correct duration/waveform and edit mode hasn't). Previously this
        // read the two flat fields, which are always undefined, so every
        // existing recording opened into edit as "0:00" with a dead Play —
        // carrying `serverPiece` through also gives the new real preview
        // cards (RecordingRegion, NEW-130) their real waveform/poster.
        const duration = kind === 'voice'
          ? (piece.voiceNote?.duration || 0)
          : (piece.videoNote?.durationSeconds || 0);
        const url = kind === 'voice'
          ? piece.voiceNote?.playbackUrl
          : piece.videoNote?.playbackUrl;
        recEntries.push({
          id: nextLocalId++,
          serverId: piece.id,
          kind,
          duration,
          durationFormatted: formatSec(duration),
          url,
          serverPiece: piece,
        });
      }
      // R-2: every server piece gets a token in order — including categories
      // without a chip representation. Dropping one would soft-delete it.
      tokens.push({ kind: 'server', id: piece.id, category: kind });
    }
    setETitle(moment.title || '');   // stored title (D-3.9), not the derived one
    setEDay(moment.dayText || '');
    setEText(txt);
    setTextPieceId(txtId);
    setOrderList(tokens);
    setEditPhotos(photoEntries);
    setEditRecordings(recEntries);
    setEditBaseline({
      title: moment.title || '',
      day: moment.dayText || '',
      text: txt,
      sig: tokens.map((t) => `s${t.id}`).join('|'),
    });
    setSaveNotice(null);
    setEditing(true);
  };

  const exitEditState = () => {
    editPhotos.forEach((p) => { if (!p.serverId) URL.revokeObjectURL(p.url); });
    editRecordings.forEach((r) => { if (!r.serverId) URL.revokeObjectURL(r.url); });
    setEditPhotos([]);
    setEditRecordings([]);
    setOrderList([]);
    setEditBaseline(null);
    setSaveNotice(null);
    setEditing(false);
  };

  // ─── Piece add/remove (page owns arrays, ids, URLs — C1 division of labour) ───
  const addPhoto = (file) => {
    const localId = nextLocalId++;
    setEditPhotos((prev) => [...prev, { id: localId, file, url: URL.createObjectURL(file) }]);
    setOrderList((prev) => [...prev, { kind: 'local', localId }]);
  };

  const addRecording = (data) => {
    const localId = nextLocalId++;
    setEditRecordings((prev) => [...prev, {
      id: localId,
      ...data,
      url: URL.createObjectURL(data.blob),
    }]);
    setOrderList((prev) => [...prev, { kind: 'local', localId }]);
  };

  const removeEditPhoto = (id) => {
    const gone = editPhotos.find((p) => p.id === id);
    if (!gone) return;
    if (!gone.serverId) URL.revokeObjectURL(gone.url);
    setEditPhotos((prev) => prev.filter((p) => p.id !== id));
    setOrderList((prev) => prev.filter((t) => (
      gone.serverId ? (t.kind !== 'server' || t.id !== gone.serverId)
                    : (t.kind !== 'local' || t.localId !== id))));
  };

  const removeEditRecording = (id) => {
    const gone = editRecordings.find((r) => r.id === id);
    if (!gone) return;
    if (!gone.serverId) URL.revokeObjectURL(gone.url);
    setEditRecordings((prev) => prev.filter((r) => r.id !== id));
    setOrderList((prev) => prev.filter((t) => (
      gone.serverId ? (t.kind !== 'server' || t.id !== gone.serverId)
                    : (t.kind !== 'local' || t.localId !== id))));
  };

  // ─── Exits (D-4.9/O-7; S-10 guard; B-3.6 destinations) ───
  const handleCancelEdit = () => {
    if (dirty) { setPendingExit('view'); setShowLeaveConfirm(true); return; }
    exitEditState();
  };

  // F-D2(a): element-anchored return to the feed, no pulse.
  const handleBack = useCallback(() => {
    if (editing && dirty) { setPendingExit('feed'); setShowLeaveConfirm(true); return; }
    navigate(`/spaces/${spaceId}`, {
      state: { memoryId: momentId, scrollOnly: true },
    });
  }, [navigate, spaceId, momentId, editing, dirty]);

  const handleLeaveConfirmed = () => {
    setShowLeaveConfirm(false);
    if (pendingExit === 'feed') {
      exitEditState();
      navigate(`/spaces/${spaceId}`, { state: { memoryId: momentId, scrollOnly: true } });
    } else {
      exitEditState();
    }
  };

  // ─── Save (uploads → assemble → PATCH → response becomes the view) ───
  const handleSaveEdit = async () => {
    if (savingEdit) return;
    setSaveNotice(null);
    if (!dirty) return;
    if (pieceCount === 0) {
      // Text-clearing leak past A-6 — pre-empt the server's MUST_KEEP_ONE_PIECE
      // with the N1 line, no PATCH (create-parity posture).
      setSaveNotice('empty');
      return;
    }
    setSavingEdit(true);
    try {
      const api = createApiClient(getAccessTokenSilently);
      const localSpecs = new Map();
      for (const p of editPhotos) {
        if (p.serverId) continue;
        const mimeType = normalizeMime(p.file.type, 'image/jpeg');
        const uploadData = await api.post('/media/upload-url', { spaceId, mimeType, mediaType: 'photo' });
        await api.putS3(uploadData.uploadUrl, p.file, mimeType);
        localSpecs.set(p.id, { type: 'photo', s3Key: uploadData.s3Key });
      }
      for (const r of editRecordings) {
        if (r.serverId) continue;
        const isVoice = r.kind === 'voice';
        const mimeType = normalizeMime(r.mimeType, isVoice ? 'audio/webm' : 'video/webm');
        const uploadData = await api.post('/media/upload-url', {
          spaceId, mimeType, mediaType: isVoice ? 'voice' : 'video',
        });
        await api.putS3(uploadData.uploadUrl, r.blob, mimeType);
        localSpecs.set(r.id, {
          type: isVoice ? 'voice' : 'video',
          s3Key: uploadData.s3Key,
          mimeType,
          duration: Math.round(r.duration),
        });
      }
      const pieces = assemblePatchPieces(orderList, {
        text: eText,
        baselineText: editBaseline ? editBaseline.text : '',
        textPieceId,
        localSpecs,
      });
      const fresh = await api.patch(`/moments/${momentId}`, {
        title: eTitle.trim(),
        dayText: eDay.trim(),
        pieces,
      });
      // F-B4(a)/AIP-134: the PATCH response is the fully-populated resource —
      // it becomes the view state directly. No refetch, no navigation.
      setMoment(fresh);
      invalidateMemoriesCache(spaceId);
      setSavingEdit(false);
      exitEditState();
    } catch (err) {
      console.error('MomentDetailPage save error:', err);
      setSavingEdit(false);
      setSaveNotice(err?.error === 'TOO_MANY_PIECES' ? 'cap' : 'failed');
    }
  };

  // ─── Whole-memory remove (D-4.16). Author-only is enforced server-side
  //     (uniform 404); the page only renders when moments_enabled. On success
  //     the moment leaves the feed; DS-13 confirmation rides the nav state. ───
  const handleConfirmRemove = async () => {
    if (removing) return;
    setRemoving(true);
    setRemoveError(false);
    try {
      const api = createApiClient(getAccessTokenSilently);
      await api.post(`/moments/${momentId}/delete`);
      invalidateMemoriesCache(spaceId);
      navigate(`/spaces/${spaceId}`, {
        state: { memoryId: momentId, scrollOnly: true, removedToast: true },
      });
    } catch (err) {
      console.error('MomentDetailPage remove error:', err);
      setRemoving(false);
      setShowRemoveConfirm(false);
      setRemoveError(true); // DS-10 inline, near the action
    }
  };

  // ─── Per-piece favorite toggle (v0.7). Optimistic, mirrors
  //     MemoryFeed.handleFavorite: flip locally → POST /memories/:id/favorite
  //     (owner-scoped server-side) → reconcile; silent revert on failure
  //     (no user-facing copy, matching the deployed feed behavior). ───
  const handleToggleFavorite = useCallback(async (pieceId, currentFav) => {
    const newVal = !currentFav;
    setMoment((prev) => (prev ? {
      ...prev,
      pieces: (prev.pieces || []).map((p) => (p.id === pieceId ? { ...p, isFavorite: newVal } : p)),
    } : prev));
    try {
      const api = createApiClient(getAccessTokenSilently);
      const result = await api.post(`/memories/${pieceId}/favorite`);
      if (result && result.isFavorite !== undefined) {
        setMoment((prev) => (prev ? {
          ...prev,
          pieces: (prev.pieces || []).map((p) => (p.id === pieceId ? { ...p, isFavorite: result.isFavorite } : p)),
        } : prev));
      }
    } catch (err) {
      console.error('Favorite toggle failed:', err);
      setMoment((prev) => (prev ? {
        ...prev,
        pieces: (prev.pieces || []).map((p) => (p.id === pieceId ? { ...p, isFavorite: !newVal } : p)),
      } : prev));
    }
  }, [getAccessTokenSilently]);

  // ─── Per-piece sharing toggle (v0.8). Optimistic flip of memory.isPrivate,
  //     mirrors handleToggleFavorite but uses PATCH /memories/:id {isPrivate}
  //     (owner-scoped server-side) and — because sharing changes who can see a
  //     piece — surfaces a quiet failure line and invalidates the feed cache so
  //     the Private/Shared tabs reflect the change. ───
  const handleToggleShare = useCallback(async (pieceId, currentPrivate) => {
    const newVal = !currentPrivate;
    setShareNotice(false);
    setMoment((prev) => (prev ? {
      ...prev,
      pieces: (prev.pieces || []).map((p) => (p.id === pieceId ? { ...p, isPrivate: newVal } : p)),
    } : prev));
    try {
      const api = createApiClient(getAccessTokenSilently);
      const result = await api.patch(`/memories/${pieceId}`, { isPrivate: newVal });
      if (result && result.isPrivate !== undefined) {
        setMoment((prev) => (prev ? {
          ...prev,
          pieces: (prev.pieces || []).map((p) => (p.id === pieceId ? { ...p, isPrivate: result.isPrivate } : p)),
        } : prev));
      }
      invalidateMemoriesCache(spaceId);
    } catch (err) {
      console.error('Share toggle failed:', err);
      setMoment((prev) => (prev ? {
        ...prev,
        pieces: (prev.pieces || []).map((p) => (p.id === pieceId ? { ...p, isPrivate: currentPrivate } : p)),
      } : prev));
      setShareNotice(true);
    }
  }, [getAccessTokenSilently, spaceId]);

  // ─── v1.0 (W2) per-piece tag add. Server assigns the id, so we reconcile from
  //     the returned tags rather than optimistically (a 409 at the cap is surfaced
  //     as a quiet line). PATCH /memories/:id { addTag } (creator-scoped server-side). ───
  const handleAddTag = useCallback(async (pieceId, label) => {
    const clean = (label || '').trim();
    if (!clean) return;
    setTagNotice(null);
    try {
      const api = createApiClient(getAccessTokenSilently);
      const result = await api.patch(`/memories/${pieceId}`, { addTag: clean });
      if (result && Array.isArray(result.tags)) {
        setMoment((prev) => (prev ? {
          ...prev,
          pieces: (prev.pieces || []).map((p) => (p.id === pieceId ? { ...p, tags: result.tags } : p)),
        } : prev));
      }
      invalidateMemoriesCache(spaceId);
    } catch (err) {
      console.error('Add tag failed:', err);
      setTagNotice(err && err.status === 409 ? 'limit' : true);
    }
  }, [getAccessTokenSilently, spaceId]);

  // ─── v1.0 (W2) per-piece tag remove. Optimistic (we hold the id) + rollback. ───
  const handleRemoveTag = useCallback(async (pieceId, tagId) => {
    setTagNotice(null);
    let prevTags = null;
    setMoment((prev) => {
      if (!prev) return prev;
      return { ...prev, pieces: (prev.pieces || []).map((p) => {
        if (p.id !== pieceId) return p;
        prevTags = p.tags || [];
        return { ...p, tags: (p.tags || []).filter((t) => t.id !== tagId) };
      }) };
    });
    try {
      const api = createApiClient(getAccessTokenSilently);
      const result = await api.patch(`/memories/${pieceId}`, { removeTagId: tagId });
      if (result && Array.isArray(result.tags)) {
        setMoment((prev) => (prev ? {
          ...prev,
          pieces: (prev.pieces || []).map((p) => (p.id === pieceId ? { ...p, tags: result.tags } : p)),
        } : prev));
      }
      invalidateMemoriesCache(spaceId);
    } catch (err) {
      console.error('Remove tag failed:', err);
      setMoment((prev) => (prev ? {
        ...prev,
        pieces: (prev.pieces || []).map((p) => (p.id === pieceId && prevTags ? { ...p, tags: prevTags } : p)),
      } : prev));
      setTagNotice(true);
    }
  }, [getAccessTokenSilently, spaceId]);

  // Client-side autocomplete vocabulary: distinct labels across the moment's loaded pieces.
  const tagSuggestions = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const p of (moment?.pieces || [])) {
      for (const t of (p.tags || [])) {
        const k = t.label.toLowerCase();
        if (!seen.has(k)) { seen.add(k); out.push(t.label); }
      }
    }
    return out;
  }, [moment]);

  const backLink = (
    <button className={styles.backLink} onClick={handleBack}>
      Back to memories
    </button>
  );

  if (status === 'loading') {
    return (
      <div className={styles.loading} aria-hidden="true">
        <div className={styles.loadingDot} />
      </div>
    );
  }

  if (status === 'notfound' || status === 'error') {
    return (
      <div className={styles.page}>
        <div className={styles.column}>
          {backLink}
          <p className={styles.stateText}>
            {status === 'notfound'
              ? 'We couldn’t find this memory. If you think it should be here, you can reach Support.'
              : 'We weren’t able to open this memory. When you’re ready, please try again.'}
          </p>
        </div>
      </div>
    );
  }

  // ─── Edit state: the capture layout, in place (W-1) ───
  if (editing) {
    return (
      <div className={styles.page}>
        <div className={`${styles.column} ${tokenStyles.tokens}`}>
          {backLink}

          <label className={styles.hiddenLabel} htmlFor="md-title">Name this memory</label>
          <input
            id="md-title"
            className={styles.titleInput}
            type="text"
            value={eTitle}
            onChange={(e) => { if (e.target.value.length <= TITLE_MAX) setETitle(e.target.value); }}
            placeholder="Name this memory"
            aria-label="Memory title"
            maxLength={TITLE_MAX}
          />
          <p className={styles.editLabel}>{EDIT_LABEL}</p>
          <input
            className={styles.dayInput}
            type="text"
            maxLength={DAY_MAX}
            value={eDay}
            onChange={(e) => setEDay(e.target.value)}
            aria-label="When was this? Optional."
            placeholder="When was this?"
            autoComplete="off"
            autoCapitalize="none"
            spellCheck="false"
          />

          <WriteRegion
            text={eText}
            onChange={setEText}
            atCap={atCap}
            hideRemove={soleRemaining}
          />
          <PhotoRegion
            photos={editPhotos}
            onAddPhoto={addPhoto}
            onRemovePhoto={removeEditPhoto}
            atCap={atCap}
            saving={savingEdit}
            hideRemove={soleRemaining}
          />
          <RecordingRegion
            kind="voice"
            recordings={voiceEditRecordings}
            onAddRecording={addRecording}
            onRemoveRecording={removeEditRecording}
            atCap={atCap}
            saving={savingEdit}
            hideRemove={soleRemaining}
            getApi={getApi}
            theme={voiceTheme}
          />
          <RecordingRegion
            kind="video"
            recordings={videoEditRecordings}
            onAddRecording={addRecording}
            onRemoveRecording={removeEditRecording}
            atCap={atCap}
            saving={savingEdit}
            hideRemove={soleRemaining}
          />
          {atCap && (
            <p className={amStyles.capLine}>
              A memory can hold up to 10 things. You can save this one and add more in a new memory
            </p>
          )}

          <div className={styles.actionArea}>
            {saveNotice === 'empty' && (
              <p className={amStyles.permissionLine}>{EMPTY_SAVE_LINE}</p>
            )}
            {saveNotice === 'failed' && (
              <p className={amStyles.permissionLine} role="alert">{SAVE_CHANGES_FAILED_LINE}</p>
            )}
            {saveNotice === 'cap' && (
              <p className={amStyles.permissionLine} role="alert">{CAP_NOTICE_LINE}</p>
            )}
            <button
              type="button"
              className={amStyles.saveBtn}
              onClick={handleSaveEdit}
              disabled={savingEdit}
              aria-label="Save changes"
            >
              {savingEdit ? 'Saving…' : 'Save changes'}
            </button>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={handleCancelEdit}
              disabled={savingEdit}
            >
              Cancel
            </button>
          </div>
        </div>

        {/* S-10 (copy v0.5; the line MemoryDetailPage already ships — F-2).
            Seventh consumer of the shared modal (v1.1). */}
        <LeaveConfirmModal
          open={showLeaveConfirm}
          message={LEAVE_MESSAGE}
          icon="edit"
          onKeepEditing={() => setShowLeaveConfirm(false)}
          onLeave={handleLeaveConfirmed}
        />
      </div>
    );
  }

  // ─── View state — byte-identical v0.1 rendering + the v0.2 action row ───
  const title = deriveMomentTitle(moment, { withDate: true });
  const pieces = moment.pieces || [];
  const dayText = (moment.dayText || '').trim(); // F-D3(a): entered date only
  const dateParseable = dayText && !Number.isNaN(Date.parse(dayText));

  return (
    <div className={styles.page}>
      <div className={styles.column}>
      {/* v0.4 (D-4.16): actions lift out of the reading body into a top bar —
          Back left, Edit + Remove right; Remove in the quiet-red register. */}
      <div className={styles.topBar}>
        {backLink}
        <div className={styles.viewActions}>
          <button className={styles.editLink} onClick={handleEnterEdit}>
            Edit
          </button>
          <button
            className={styles.removeLink}
            onClick={() => { setRemoveError(false); setShowRemoveConfirm(true); }}
          >
            Remove memory
          </button>
        </div>
      </div>
      {removeError && (
        <p className={amStyles.permissionLine} role="alert">{REMOVE_FAILED_LINE}</p>
      )}
      {shareNotice && (
        <p className={amStyles.permissionLine} role="alert">{SHARE_FAILED_LINE}</p>
      )}
      {tagNotice && (
        <p className={amStyles.permissionLine} role="alert">
          {tagNotice === 'limit'
            ? 'You can add up to five tags to a memory.'
            : "We weren't able to save that tag. When you're ready, please try again."}
        </p>
      )}
      <h1 className={styles.title}>{title}</h1>
      <div className={styles.pieces}>
        {feedRedesignEnabled ? (
          <MomentReadingView
            moment={moment}
            title={title}
            voiceTheme={voiceTheme}
            getApi={getApi}
            redesign
            onToggleFavorite={handleToggleFavorite}
            onToggleShare={handleToggleShare}
            memberTagsEnabled={memberTagsEnabled}
            onAddTag={handleAddTag}
            onRemoveTag={handleRemoveTag}
            tagSuggestions={tagSuggestions}
          />
        ) : (
          pieces.map((piece) => {
            const kind = cat(piece);
            if (kind === 'text' && piece.note) {
              return (
                <div key={piece.id} className={styles.textPiece}>
                  {splitParagraphs(piece.note).map((para, i) => (
                    <p key={i} className={styles.paragraph}>{para}</p>
                  ))}
                </div>
              );
            }
            if (kind === 'photo') {
              return (
                <div key={piece.id} className={styles.photoPiece}>
                  <MomentPhoto piece={piece} alt={title} />
                </div>
              );
            }
            if (kind === 'voice') {
              return (
                <div key={piece.id} className={styles.presencePiece}>
                  <VoiceCard memory={piece} getApi={getApi} theme={voiceTheme} embedded />
                </div>
              );
            }
            if (kind === 'video') {
              return (
                <div key={piece.id} className={styles.presencePiece}>
                  <VideoCard memory={piece} embedded />
                </div>
              );
            }
            return null; // 'file' etc.: no renderer this pass — recorded, not invented
          })
        )}
      </div>
      {dayText && (
        dateParseable ? (
          <time className={styles.date} dateTime={new Date(dayText).toISOString().slice(0, 10)}>
            {dayText}
          </time>
        ) : (
          <p className={styles.date} aria-label={`Date: ${dayText}`}>{dayText}</p>
        )
      )}
      </div>

      {/* v0.4: whole-memory remove confirmation (copy v0.12 DS-2..DS-5). */}
      <DeleteConfirmModal
        open={showRemoveConfirm}
        title="Remove this memory?"
        message="This whole memory will move to Recently Deleted. You can bring it back anytime in the next 30 days."
        confirmLabel="Remove memory"
        cancelLabel="Keep memory"
        onConfirm={handleConfirmRemove}
        onCancel={() => setShowRemoveConfirm(false)}
        loading={removing}
      />
    </div>
  );
}
